/* ================================
  Cart Drawer Component
================================ */
class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (e) => {
      if (e.code === 'Escape') {
        this.close();
      }
    });

    this.querySelector('#CartDrawer-Overlay')?.addEventListener(
      'click',
      this.close.bind(this)
    );

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (e) => {
      e.preventDefault();
      // FIX: always reset to the Shopping Bag tab when opening via the header cart icon
      window.switchToTab && window.switchToTab('bag');
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (e) => {
      if (e.code.toUpperCase() === 'SPACE' || e.code === 'Enter') {
        e.preventDefault();
        window.switchToTab && window.switchToTab('bag');
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.activeElement = triggeredBy;
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn =
          document.getElementById('CartDrawer');

        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close');

        if (
          typeof trapFocus === 'function' &&
          containerToTrapFocusOn &&
          focusElement
        ) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');

    setTimeout(() => {
      window.reloadCartDrawerRecommendations?.();
    }, 200);

    setTimeout(() => {
      window.updateFreeShippingBar?.();
    }, 250);
  }

  close() {
    this.classList.remove('active');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }

    document.body.classList.remove('overflow-hidden');
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const element = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!element) return;

      const sectionHTML = parsedState.sections?.[section.id];

      if (!sectionHTML) return;

      element.innerHTML = this.getSectionInnerHTML(
        sectionHTML,
        section.selector
      );
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener(
        'click',
        this.close.bind(this)
      );

      window._cartDrawerAfterRender?.();

      this.open();
    }, 100);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const element = doc.querySelector(selector);

    return element ? element.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);


/* ================================
  Cart Drawer Items
================================ */
class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  updateQuantity(line, quantity, name) {
    super.updateQuantity(line, quantity, name);

    setTimeout(() => {
      window.refreshCartDrawer?.();
      window.reloadCartDrawerRecommendations?.();
    }, 1000);
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);


/* ================================
  Cart Drawer After Render
================================ */
window._cartDrawerAfterRender = function () {
  document
    .querySelector('#CartDrawer-Overlay')
    ?.addEventListener('click', () => {
      document.querySelector('cart-drawer')?.close();
    });

  if (typeof initShippingToggle === 'function') {
    initShippingToggle();
  }

  if (typeof syncToggleWithCart === 'function') {
    syncToggleWithCart();
  }

  setTimeout(() => {
    window.updateFreeShippingBar?.();
  }, 100);

  setTimeout(() => {
    window.reloadCartDrawerRecommendations?.();
  }, 300);
};


/* ================================
  Refresh Cart Drawer
================================ */
window.refreshCartDrawer = async function () {
  try {
    const response = await fetch(
      '/?sections=cart-drawer,cart-icon-bubble',
      {
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error('Failed to refresh cart drawer');
    }

    const sections = await response.json();

    /* Cart Drawer */
    const drawer = document.querySelector('#CartDrawer');

    if (drawer && sections['cart-drawer']) {
      const doc = new DOMParser().parseFromString(
        sections['cart-drawer'],
        'text/html'
      );

      const newDrawer = doc.querySelector('#CartDrawer');

      if (newDrawer) {
        drawer.innerHTML = newDrawer.innerHTML;
      }
    }

    /* Cart Bubble */
    const bubble = document.querySelector('#cart-icon-bubble');

    if (bubble && sections['cart-icon-bubble']) {
      const doc = new DOMParser().parseFromString(
        sections['cart-icon-bubble'],
        'text/html'
      );

      const newBubble = doc.querySelector('#cart-icon-bubble');

      if (newBubble) {
        bubble.innerHTML = newBubble.innerHTML;
      }
    }

    /* Reinitialize after DOM replacement */
    setTimeout(() => {
      if (typeof initShippingToggle === 'function') {
        initShippingToggle();
      }

      if (typeof syncToggleWithCart === 'function') {
        syncToggleWithCart();
      }

      if (typeof initRecommendationSlider === 'function') {
        initRecommendationSlider();
      }

      window.reloadCartDrawerRecommendations?.();
    }, 150);

  } catch (error) {
    console.error('Cart drawer refresh failed:', error);
  }
};


/* ================================
  Recommendation Products
================================ */
const currencySymbol = '₹';

function formatMoney(cents) {
  if (cents == null) return '';

  let amount = (cents / 100).toFixed(2);

  amount = amount.replace(
    /\B(?=(\d{3})+(?!\d))/g,
    ','
  );

  return currencySymbol + amount;
}

window.loadCartDrawerRecommendations = function (productId) {
  const container = document.getElementById('cart-recommendations');

  if (!container || !productId) return;

  const track = container.querySelector(
    '.recommendations-wrapper'
  );

  if (!track) return;

  fetch(
    `/recommendations/products.json?product_id=${productId}&limit=6`
  )
    .then((res) => {
      if (!res.ok) {
        throw new Error('Recommendation request failed');
      }

      return res.json();
    })
    .then((data) => {
      const products = (data.products || []).filter(
        (product) =>
          product.handle !== 'shipping-protection' &&
          product.variants?.length
      );

      if (!products.length) {
        container.style.display = 'none';
        return;
      }

      container.style.display = 'block';
      track.innerHTML = '';

      products.forEach((product) => {
        const image = product.featured_image || '';
        const title = product.title || '';

        const variant =
          product.variants.find((v) => v.available) ||
          product.variants[0];

        if (!variant) return;

        track.insertAdjacentHTML(
          'beforeend',
          `
            <div class="cd-rec-card">
              <div class="cd-rec-card__img-wrap">

                ${
                  image
                    ? `<img
                        src="${image}"
                        alt="${title.replace(/"/g, '&quot;')}"
                        loading="lazy"
                      >`
                    : ''
                }

                <button
                  type="button"
                  class="cd-rec-card__add-btn recommandation_add_product"
                  data-variant-id="${variant.id}"
                >
                  + ADD
                </button>

              </div>

              <span class="cd-rec-card__title">
                ${title}
              </span>

              <span class="cd-rec-card__variant">
                ${variant.title}
              </span>

              <span class="price">
                ${formatMoney(product.price)}
              </span>
            </div>
          `
        );
      });

      setTimeout(() => {
        initRecommendationSlider?.();
      }, 100);
    })
    .catch((error) => {
      console.error('Recommendation fetch error:', error);
      container.style.display = 'none';
    });
};


/* ================================
  Reload Recommendations
================================ */
window.reloadCartDrawerRecommendations = function () {
  fetch('/cart.js')
    .then((res) => res.json())
    .then((cart) => {
      if (!cart.items.length) return;

      const lastProductId =
        cart.items[cart.items.length - 1].product_id;

      window.loadCartDrawerRecommendations?.(
        lastProductId
      );
    })
    .catch((error) => {
      console.error('Cart recommendation reload error:', error);
    });
};


/* ================================
  Recommendation Add To Cart
================================ */
document.addEventListener('click', async function (e) {
  const button = e.target.closest(
    '.recommandation_add_product'
  );

  if (!button) return;

  e.preventDefault();

  const variantId = button.dataset.variantId;

  if (!variantId) return;

  const originalText = button.innerHTML;

  button.disabled = true;
  button.innerHTML = '...';

  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: Number(variantId),
        quantity: 1
      })
    });

    if (!response.ok) {
      throw new Error('Failed to add product');
    }

    await response.json();

    button.innerHTML = '✓';
    button.classList.add('is-added');

    await window.refreshCartDrawer();

    document
      .querySelector('cart-drawer')
      ?.open();

    setTimeout(() => {
      button.disabled = false;
      button.innerHTML = originalText;
      button.classList.remove('is-added');
    }, 1500);

  } catch (error) {
    console.error('Recommendation add error:', error);

    button.disabled = false;
    button.innerHTML = originalText;
  }
});


/* ================================
  Shipping Protection
================================ */
window.shippingVariantId = 48950224912624;

function initShippingToggle() {
  const toggle = document.getElementById(
    'shippingProtectionToggle'
  );

  if (!toggle) return;

  toggle.removeEventListener(
    'change',
    handleShippingToggle
  );

  toggle.addEventListener(
    'change',
    handleShippingToggle
  );
}

function handleShippingToggle(e) {
  if (e.target.checked) {
    addShippingProtection();
  } else {
    removeShippingProtection();
  }
}

function syncToggleWithCart() {
  const toggle = document.getElementById(
    'shippingProtectionToggle'
  );

  if (!toggle) return;

  fetch('/cart.js')
    .then((res) => res.json())
    .then((cart) => {
      const item = cart.items.find(
        (item) =>
          Number(item.variant_id) ===
          Number(window.shippingVariantId)
      );

      toggle.checked = !!item;
    });
}

function addShippingProtection() {
  fetch('/cart/add.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    body: JSON.stringify({
      id: window.shippingVariantId,
      quantity: 1
    })
  })
    .then((res) => {
      if (!res.ok) {
        throw new Error('Failed to add shipping protection');
      }

      return res.json();
    })
    .then(() => {
      window.refreshCartDrawer?.();
    })
    .catch((error) => {
      console.error('Shipping protection error:', error);
    });
}

function removeShippingProtection() {
  fetch('/cart.js')
    .then((res) => res.json())
    .then((cart) => {
      const item = cart.items.find(
        (item) =>
          Number(item.variant_id) ===
          Number(window.shippingVariantId)
      );

      if (!item) return;

      return fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: item.key,
          quantity: 0
        })
      });
    })
    .then((res) => {
      if (res && !res.ok) {
        throw new Error(
          'Failed to remove shipping protection'
        );
      }

      window.refreshCartDrawer?.();
    })
    .catch((error) => {
      console.error(
        'Remove shipping protection error:',
        error
      );
    });
}


/* ================================
  Recommendation Slider
================================ */
function initRecommendationSlider() {
  const container =
    document.getElementById('cart-recommendations');

  if (!container) return;

  const wrapper =
    container.querySelector(
      '.recommendations-wrapper'
    );

  if (!wrapper) return;

  container
    .querySelectorAll('.rec-slider-btn')
    .forEach((el) => el.remove());

  const prevBtn =
    document.createElement('button');

  prevBtn.className =
    'rec-slider-btn rec-slider-btn--prev';

  prevBtn.type = 'button';

  prevBtn.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2.5"
      stroke-linecap="round"
      stroke-linejoin="round">
      <polyline points="15 18 9 12 15 6"></polyline>
    </svg>
  `;

  const nextBtn =
    document.createElement('button');

  nextBtn.className =
    'rec-slider-btn rec-slider-btn--next';

  nextBtn.type = 'button';

  nextBtn.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2.5"
      stroke-linecap="round"
      stroke-linejoin="round">
      <polyline points="9 18 15 12 9 6"></polyline>
    </svg>
  `;

  const header =
    container.querySelector('.rec-slider-header');

  if (header) {
    header.appendChild(prevBtn);
    header.appendChild(nextBtn);
  }

  const scrollAmount = 280;

  prevBtn.addEventListener('click', () => {
    wrapper.scrollBy({
      left: -scrollAmount,
      behavior: 'smooth'
    });
  });

  nextBtn.addEventListener('click', () => {
    wrapper.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });
  });

  function updateArrows() {
    const atStart = wrapper.scrollLeft <= 5;

    const atEnd =
      wrapper.scrollLeft +
        wrapper.clientWidth >=
      wrapper.scrollWidth - 5;

    prevBtn.style.opacity = atStart ? '0.3' : '1';
    nextBtn.style.opacity = atEnd ? '0.3' : '1';

    prevBtn.style.pointerEvents =
      atStart ? 'none' : 'auto';

    nextBtn.style.pointerEvents =
      atEnd ? 'none' : 'auto';
  }

  wrapper.addEventListener(
    'scroll',
    updateArrows,
    { passive: true }
  );

  updateArrows();

  /* Drag Scroll */
  let isDown = false;
  let startX = 0;
  let scrollLeft = 0;

  wrapper.addEventListener('mousedown', (e) => {
    isDown = true;

    startX =
      e.pageX -
      wrapper.offsetLeft;

    scrollLeft =
      wrapper.scrollLeft;

    wrapper.style.cursor = 'grabbing';
  });

  wrapper.addEventListener('mouseleave', () => {
    isDown = false;
    wrapper.style.cursor = 'grab';
  });

  wrapper.addEventListener('mouseup', () => {
    isDown = false;
    wrapper.style.cursor = 'grab';
  });

  wrapper.addEventListener('mousemove', (e) => {
    if (!isDown) return;

    e.preventDefault();

    const x =
      e.pageX -
      wrapper.offsetLeft;

    const walk =
      (x - startX) * 1.5;

    wrapper.scrollLeft =
      scrollLeft - walk;
  });
}


/* ================================
  DOM Ready
================================ */
document.addEventListener('DOMContentLoaded', () => {
  initShippingToggle();
  syncToggleWithCart();
  initRecommendationSlider();
});


/* ================================
  Outside-click closes the cart drawer
  (delegated at document level so it survives
  the drawer's innerHTML being replaced by AJAX refreshes)
================================ */
if (!window.__cartDrawerOutsideClickBound) {
  window.__cartDrawerOutsideClickBound = true;

  document.addEventListener('click', function (e) {
    var drawer = document.querySelector('cart-drawer.active');
    if (!drawer) return;

    // Never close while interacting with the wishlist "move/remove" confirm popup
    if (e.target.closest('#WishlistConfirmModal')) return;

    // Never treat the cart icon trigger as an "outside" click
    if (e.target.closest('#cart-icon-bubble')) return;

    var inner = drawer.querySelector('.drawer__inner');

    // Click landed inside the drawer's visible content -> do nothing
    if (inner && inner.contains(e.target)) return;

    // Click landed on the overlay backdrop, or anywhere else outside the
    // drawer panel -> close it
    if (typeof drawer.close === 'function') {
      drawer.close();
    }
  });
}